import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Badge, Button, DataTable, Dialog, Drawer, EmptyState, Inline, Link, PageHeader, RestrictedState, SaveBar, Skeleton, Stack, StatusDot, t, Tag, Text, Textarea, toast,
} from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { Shell } from '../../components/Shell';
import { RefusalPage } from '../../components/Refusal';
import { serverStateColumn } from '../../components/ServerState';
import { PullRequestNotice, changeRef } from '../../components/catalogue/PullRequestNotice';
import { api, ApiFailure, causeOf, unwrap } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useFillHeight } from '../../lib/useFillHeight';
import { useEnvironmentName } from '../../lib/environments';
import { FULL_HEIGHT } from '../../lib/tables';

type Links = maintenance.components['schemas']['ServerLinks'];
type Row = Links['rows'][number];
type Candidate = maintenance.components['schemas']['LinkCandidate'];
type Rule = maintenance.components['schemas']['LinkRule'];

const same = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

/**
 * « Serveurs du projet » (SPEC-006), record page in edit mode: one cell per
 * application × environment, its servers as tags, the rules of the backend
 * (preview) in the cells, then one pull request (RG-006-11, RG-006-12).
 */
export function ProjectServersPage() {
  const envName = useEnvironmentName();
  const { project = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enums } = useSession();
  const [draft, setDraft] = useState<Record<string, string[]>>({});
  const [justifications, setJustifications] = useState<Record<string, string>>({});
  const [adding, setAdding] = useState<{ row: Row; env: string; deployment: string } | null>(null);
  const [picked, setPicked] = useState<Record<string, true>>({});
  const [showChanges, setShowChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const fill = useFillHeight();

  const links = useQuery({
    queryKey: ['server-links', project],
    queryFn: async () => unwrap(await api.maintenance.GET('/projects/{project}/server-links', { params: { path: { project } } })),
    retry: false,
    // A new project's applications arrive with the catalogue's reading of its pull requests (SPEC-006):
    // until then the page reads them again every 5 s.
    refetchInterval: query => (query.state.data && !query.state.data.rows.length ? 5_000 : false),
  });
  const current = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const row of links.data?.rows ?? []) for (const cell of Object.values(row.cells)) if (cell) map[cell.deployment] = cell.servers.map(s => s.name);
    return map;
  }, [links.data]);
  const changed = Object.fromEntries(Object.entries(draft).filter(([d, servers]) => !same(servers, current[d] ?? [])));
  const preview = useQuery({
    queryKey: ['server-links-preview', project, changed, justifications],
    queryFn: async () => unwrap(await api.maintenance.POST('/projects/{project}/server-links/preview', { params: { path: { project } }, body: { links: changed, justifications } })),
    enabled: links.isSuccess,
    placeholderData: keepPreviousData,
  });
  const candidates = useQuery({
    queryKey: ['server-links-candidates', project, adding?.deployment],
    queryFn: async () => unwrap(await api.maintenance.POST('/projects/{project}/server-links/preview', { params: { path: { project } }, body: { links: {}, candidatesFor: adding!.deployment } })),
    enabled: Boolean(adding),
  });
  useEffect(() => { setPicked({}); }, [adding]);

  const crumbs = [{ label: t('nav.projects'), href: '/projects' }, { label: links.data?.project.title ?? project, href: `/projects/${project}` }, { label: t('projectServers.crumb') }];
  if (links.error) return <Shell crumbs={crumbs}><RefusalPage error={links.error} /></Shell>;
  const data = links.data;
  // RG-006-12: nothing else is proposed while a pull request of the links is live.
  const live = data?.pullRequest && ['open', 'validation_failed'].includes(data.pullRequest.state);
  const canEdit = Boolean(data?.canEdit) && !live;
  const serversOf = (deployment: string) => draft[deployment] ?? current[deployment] ?? [];
  const rules = preview.data ?? data?.initial;
  const rulesOf = (list: Rule[] | undefined, deployment: string) => [...new Map((list ?? []).filter(r => r.deployment === deployment).map(r => [r.message, r])).values()];
  const changes = preview.data?.changes ?? [];
  const blocking = preview.data?.blocking ?? [];
  // D48: a deployment's environment shows its name; a server's, its label.
  const serverEnvLabel = (env: string) => enums.environments.find(e => e.value === env)?.label ?? env;
  const stateOf = (name: string) => {
    for (const row of data?.rows ?? []) for (const cell of Object.values(row.cells)) { const s = cell?.servers.find(x => x.name === name); if (s) return s.state; }
    return candidates.data?.candidates?.[adding?.deployment ?? '']?.find(c => c.server === name)?.state ?? 'unknown';
  };

  // A refused rule that asks for a justification (RG-006) puts a field under the cell's line.
  const justifying = (data?.rows ?? []).some(row => Object.values(row.cells).some(c => c && rulesOf(rules?.blocking, c.deployment).some(r => r.code === 'JUSTIFICATION_REQUIRED')));

  const cell = (row: Row, env: string) => {
    const c = row.cells[env];
    if (!c) return <Text variant="body-sm" tone="mute">{t('project.matrix.notDeployed')}</Text>;
    const servers = serversOf(c.deployment);
    const warnings = rulesOf(rules?.warnings, c.deployment);
    const refused = rulesOf(rules?.blocking, c.deployment);
    const meta = (name: string) => enums.states.find(s => s.value === stateOf(name));
    const justify = refused.some(r => r.code === 'JUSTIFICATION_REQUIRED');
    // One line: the servers, the rules' warnings and refusals, then « Ajouter »; cut at the column's edge.
    const line = (
      <Inline gap={4} align="center" wrap={false}>
        {servers.map(name => (
          <Tag key={name} onRemove={canEdit ? () => setDraft(d => ({ ...d, [c.deployment]: servers.filter(s => s !== name) })) : undefined}>
            <Inline gap={4} align="center" wrap={false}>
              <StatusDot tone={meta(name)?.tone ?? 'neutral'} variant={meta(name)?.tone === 'neutral' ? 'ring' : 'solid'} label={meta(name)?.label ?? ''} tooltip={false} />
              <span>{name}</span>
            </Inline>
          </Tag>
        ))}
        {warnings.map(w => <Badge key={w.message} tone="warn" size="sm">{w.message}</Badge>)}
        {refused.map(r => <Badge key={r.message} tone="neg" size="sm">{r.message}</Badge>)}
        {canEdit && (
          <Button variant="ghost" size="sm" icon="add" onClick={() => setAdding({ row, env, deployment: c.deployment })}
            aria-label={t('projectServers.addTo', { application: row.application, environment: envName(env) })}>{t('projectServers.add')}</Button>
        )}
      </Inline>
    );
    // The justification is a field to fill: only then does the row grow (see rowHeight below).
    return justify ? (
      <Stack gap={8}>
        {line}
        <Textarea label={t('projectServers.justification')} rows={2} maxLength={300}
          value={justifications[c.deployment] ?? ''} onChange={v => setJustifications(j => ({ ...j, [c.deployment]: v }))} />
      </Stack>
    ) : line;
  };

  const save = async () => {
    setSaving(true);
    setFailure(null);
    try {
      const { pullRequest } = unwrap(await api.maintenance.PUT('/projects/{project}/server-links', { params: { path: { project } }, body: { links: changed, justifications } }));
      toast({ title: t('projectServers.opened', { ref: changeRef(pullRequest) }), tone: 'pos' });
      setDraft({});
      setJustifications({});
      await queryClient.invalidateQueries({ queryKey: ['server-links', project] });
    } catch (e) {
      setFailure(e instanceof ApiFailure ? e : null);
    } finally {
      setSaving(false);
    }
  };

  const addable = (candidates.data?.candidates?.[adding?.deployment ?? ''] ?? []).filter(c => !serversOf(adding?.deployment ?? '').includes(c.server));
  const chosen = Object.keys(picked).filter(k => addable.some(c => c.server === k && c.allowed));
  const placement = (c: Candidate) => (c.clients === 0 ? t('servers.unassigned') : c.clients === 1 ? t('project.servers.dedicated') : t('project.servers.shared', { n: c.clients }));

  const footer = canEdit ? (
    <SaveBar
      count={changes.length}
      message={t('projectServers.unsaved', { n: changes.length })}
      saveLabel={t('projectServers.save', { n: preview.data?.pullRequests ?? 1 })}
      cancelLabel={t('projectServers.cancel')}
      saving={saving}
      saveDisabled={blocking.length > 0 || preview.isFetching}
      onSave={save}
      onCancel={() => { setDraft({}); setJustifications({}); setFailure(null); }}
    />
  ) : undefined;

  return (
    <Shell crumbs={crumbs} footer={footer}>
      {!data ? (
        <Skeleton lines={8} />
      ) : (
        <Stack gap={24}>
          <PageHeader
            title={t('projectServers.title', { project: data.project.title })}
            subtitle={t('projectServers.subtitle')}
            actions={
              <Inline gap={12} align="center">
                {!data.canEdit && <RestrictedState variant="chip" label={t('projectServers.readOnly')} />}
                {/* DSM gap: the SaveBar has no slot for a secondary link (its ground is inverted), so the list of changes opens from here. */}
                {changes.length > 0 && <Button variant="secondary" size="sm" icon="list" onClick={() => setShowChanges(true)}>{t('projectServers.seeChanges')}</Button>}
                <Link href="/servers/import" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); navigate('/servers/import'); }}>{t('projectServers.missingServer')}</Link>
              </Inline>
            }
          />
          {data.healthBlocked && <Alert tone="neg">{t('projectServers.healthBlocked')}</Alert>}
          {live && data.canEdit && <Alert tone="warn">{t('projectServers.alreadyOpen', { number: data.pullRequest!.number })}</Alert>}
          <PullRequestNotice pullRequest={data.pullRequest} />
          {failure && <Alert tone="neg" title={failure.body.message}>{failure.body.hint}{causeOf(failure) ? ` ${t('errors.cause', { cause: causeOf(failure)! })}` : ''}</Alert>}
          <div ref={fill[0]}>
          <DataTable
            height={fill[1]}
            label={t('projectServers.table')}
            rows={data.rows}
            rowKey="application"
            toolbar={false}
            rowHeight={justifying ? 'auto' : undefined}
            density="compact"
            columns={[
              { key: 'application', label: t('project.matrix.application'), pinned: 'left', width: 220, mono: true },
              { key: 'role', label: t('project.matrix.role'), width: 140, render: (r: Row) => enums.applicationRoles.find(x => x.value === r.role)?.label ?? r.role },
              // One column per environment; the last one takes the room left.
              ...data.environments.map(({ name: env }, i, all) => ({ key: `env-${env}`, label: env, width: 440, grow: i === all.length - 1, sortable: false, render: (r: Row) => cell(r, env) })),
            ]}
            empty={<EmptyState framed={false} icon="apps" title={t('project.matrix.empty')} />}
          />
          </div>
        </Stack>
      )}
      {adding && (
        <Dialog
          open
          size="lg"
          onClose={() => setAdding(null)}
          title={t('projectServers.addTitle', { application: adding.row.application, environment: envName(adding.env) })}
          confirmLabel={t('projectServers.addConfirm', { n: chosen.length })}
          cancelLabel={t('projectServers.cancel')}
          confirmDisabled={chosen.length === 0}
          onConfirm={() => { setDraft(d => ({ ...d, [adding.deployment]: [...serversOf(adding.deployment), ...chosen] })); setAdding(null); }}
        >
          <DataTable
            label={t('projectServers.candidates')}
            rows={addable}
            rowKey="server"
            selectable
            selected={picked}
            onSelectionChange={sel => setPicked(Object.fromEntries(Object.keys(sel).filter(k => addable.some(c => c.server === k && c.allowed)).map(k => [k, true as const])))}
            loading={candidates.isPending}
            toolbar={false}
            density="compact"
            height={FULL_HEIGHT}
            rowState={(c: Candidate) => (c.allowed ? null : 'dimmed')}
            columns={[
              serverStateColumn<Candidate>(enums, c => ({ state: c.state })),
              { key: 'server', label: t('project.servers.name'), mono: true, width: 190 },
              { key: 'environment', label: t('project.servers.environment'), width: 130, render: (c: Candidate) => serverEnvLabel(c.environment) },
              { key: 'region', label: t('projectServers.region'), mono: true, width: 100 },
              { key: 'clients', label: t('project.servers.placement'), width: 170, render: placement },
              // A refused candidate says why; beyond the dialog's width the grid scrolls sideways (DSM: never crush columns).
              { key: 'reason', label: t('projectServers.reason'), grow: true, minWidth: 220, render: (c: Candidate) => c.reason ?? '' },
            ]}
            empty={<EmptyState framed={false} icon="dns" title={t('projectServers.noCandidate')} />}
          />
        </Dialog>
      )}
      <Drawer open={showChanges} onClose={() => setShowChanges(false)} title={t('projectServers.changesTitle')} size="xl">
        <Stack gap={16}>
          {changes.map(c => (
            <Stack key={c.deployment} gap={4}>
              <Text variant="body-strong">{c.deployment}</Text>
              {c.add.map(s => <Text key={`+${s}`} variant="body-sm">{t('projectServers.added', { server: s })}</Text>)}
              {c.remove.map(s => <Text key={`-${s}`} variant="body-sm">{t('projectServers.removed', { server: s })}</Text>)}
            </Stack>
          ))}
        </Stack>
      </Drawer>
    </Shell>
  );
}
